import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { MarkdownView } from '@/components/app/MarkdownView';

/**
 * Public REST API reference. Mirrors the auth flow from Settings →
 * API tokens — every endpoint accepts `Authorization: Bearer kez_…`
 * exactly the way the browser session does. Same JSON shapes the UI
 * already consumes, so what you see in the React app's DevTools
 * network tab is the contract.
 *
 * Kept as a single static page (no OpenAPI generator) so launching
 * a customer integration doesn't need extra build steps.
 */
const API_DOCS = `
# KeenEZ REST API

KeenEZ's REST API is the same set of endpoints the web app uses.
Every controller already accepts a workspace API token in place of
the browser session cookie — there is no separate "API" surface to
maintain, so what you read in the UI's network tab is the contract.

## Authentication

Every endpoint requires a workspace API token:

\`\`\`bash
curl https://api.keenez.com/api/auth/me \\
  -H 'Authorization: Bearer kez_<your-token>'
\`\`\`

Generate a token from **Settings → API tokens** in your workspace.
Tokens act as the workspace owner; treat them like passwords.

The browser app uses a session cookie set on login, so the same
endpoints serve the UI without separate code paths.

## Base URL

\`\`\`
https://api.keenez.com/api
\`\`\`

Dev server: \`https://api.keenez.com/api\`.

## Errors

All errors are JSON:

\`\`\`json
{ "statusCode": 402, "error": "SeatLimitReached", "message": "Free workspaces are capped at 10 employees…" }
\`\`\`

Notable codes:

- **401** — missing / invalid / revoked token
- **402** — plan gate (employee limit, country limit, paid-feature on Free)
- **403** — role gate (some endpoints require owner/admin/manager)
- **409** — state conflict (e.g. delete blocked because employee has payroll history)

---

## People

### List employees
\`\`\`bash
GET /api/employees?status=active&country=JP
\`\`\`

### Create employee
\`\`\`bash
POST /api/employees
Content-Type: application/json

{
  "legalNameLocal": "山田太郎",
  "email": "taro@acme.com",
  "countryCode": "JP",
  "employmentType": "seishain",
  "hireDate": "2026-04-01"
}
\`\`\`

### Update employee
\`\`\`bash
PATCH /api/employees/:id
\`\`\`

### Terminate employee
\`\`\`bash
POST /api/employees/:id/terminate
{ "terminationDate": "2026-06-30", "reason": "End of contract" }
\`\`\`

### Hard delete (only if no history)
\`\`\`bash
DELETE /api/employees/:id
\`\`\`

### Bulk import
\`\`\`bash
POST /api/employees/bulk-import
{ "csv": "name,email,country,hire_date\\n山田太郎,taro@acme.com,JP,2026-04-01" }
\`\`\`

## Payroll

### Calculate a JP regular run
\`\`\`bash
POST /api/payroll/jp/calculate
{
  "periodYear": 2026,
  "periodMonth": 5,
  "payDate": "2026-05-25",
  "perEmployee": { "<uuid>": { "residentTaxCentsMonthly": 1500000 } }
}
\`\`\`

US / UK / DE follow the same shape (\`/api/payroll/us/calculate\`,
\`/api/payroll/uk/calculate\`, \`/api/payroll/de/calculate\`).

### JP bonus calculation
\`\`\`bash
POST /api/payroll/jp/bonus
{
  "periodYear": 2026,
  "periodMonth": 6,
  "perEmployee": { "<uuid>": { "bonusCents": 60000000 } }
}
\`\`\`

### List runs
\`\`\`bash
GET /api/payroll/runs
GET /api/payroll/runs/:id   # full breakdown + lines
\`\`\`

## Documents

\`\`\`bash
GET    /api/documents/templates       # built-in templates
GET    /api/documents                 # list
POST   /api/documents/generate        # render from template + bindings
PATCH  /api/documents/:id             # edit draft body or title
POST   /api/documents/:id/send        # mark as sent
POST   /api/documents/:id/sign        # e-sign (SHA-256 hash of body)
\`\`\`

## Year-end (Japan 年末調整)

\`\`\`bash
GET  /api/nencho/cycles
POST /api/nencho/cycles                   # open a year
POST /api/nencho/cycles/:id/inputs        # submit employee declaration
POST /api/nencho/cycles/:id/compute       # run the engine
GET  /api/nencho/cycles/:id/results
\`\`\`

## AI agents + Border Buddy

\`\`\`bash
POST /api/ai/run                          # JSON or multipart/form-data
GET  /api/ai/runs
GET  /api/ai/spend
\`\`\`

The multipart variant accepts a \`file\` field (PDF / DOCX / TXT /
MD / PNG / JPG, ≤10 MB); the server parses it to text and prepends
it to the prompt before invoking the LLM.

## Approvals

\`\`\`bash
GET  /api/approvals
POST /api/approvals                        # create approval chain
POST /api/approvals/:id/approve            # current step approves
POST /api/approvals/:id/reject             # current step rejects
\`\`\`

## Audit log

\`\`\`bash
GET /api/audit-log?prefix=pii&limit=200
\`\`\`

Every PII reveal, payroll run, employee mutation, AI run, and
approval transition lands here with the actor, IP, user-agent, and
timestamp.

## Billing

\`\`\`bash
GET  /api/billing/subscription
POST /api/billing/portal                  # Stripe Customer Portal session
GET  /api/billing/invoices
\`\`\`

## Companies

\`\`\`bash
GET   /api/companies/me
PATCH /api/companies/me                   # change AI model default + AI cap
\`\`\`

## Audit defensibility

- All authenticated requests pass through the same JWT/API-token
  guard, so audit-log entries record the acting user even on
  programmatic calls. When an API token is used, the token id + name
  are attached to the audit row so you can revoke a leaked token
  and prove which integration made the calls.
- Sensitive PII reveals (マイナンバー, SSN, bank accounts) require a
  written reason and emit a separate \`pii.reveal\` audit row with
  the IP + UA of the requester.

## Rate limiting

The default is 120 requests / minute per token. Most integrations
fit; CI exports that hit the limit should batch list endpoints with
\`?limit=500\` rather than paginating in small chunks.

## Changelog

API changes are announced on the **/changelog** page. We never
remove fields without a deprecation notice; new fields appear
without a major version bump.
`;

export function ApiDocsPage() {
  return (
    <div className="min-h-screen bg-paper-50 flex flex-col">
      <SiteHeader />
      <main className="flex-1 max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <p className="text-xs uppercase tracking-[0.18em] text-accent-600 font-semibold">REST API</p>
        <h1 className="display serif-jp mt-2 text-3xl md:text-4xl font-semibold tracking-tight text-ink-900">
          KeenEZ API reference
        </h1>
        <p className="mt-3 text-base text-ink-600 max-w-2xl">
          The same endpoints the KeenEZ web app uses. Bring your own client; we publish curl examples below. Generate a workspace token from Settings → API tokens.
        </p>
        <div className="mt-10 rounded-2xl border border-ink-200 bg-white p-6 md:p-8">
          <MarkdownView>{API_DOCS}</MarkdownView>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
